<script setup lang="ts">
import GamePlayer from '~/components/gamescreen/GamePlayer.vue'
import EnterScoreForm from '~/components/gamescreen/EnterScoreForm.vue'
import WriterSelectionModal from '~/components/modals/WriterSelectionModal.vue'
import { useAuthStore } from '~/store/authStore'
import { useUserPrintingAppStore } from '~/store/UserPrintingAppStore'

const localePath = useLocalePath()
const { $api } = useNuxtApp()

const { activePlayerId } = useTournamentHelpers()
const { callAction } = useTournamentActions()
const authStore = useAuthStore()
const { markGameAsCalled } = useGameFunctions()

const { boards, game, forDisplay = true, compact = false } = defineProps<{
  boards: Board[]
  game: Game
  forDisplay?: boolean
  compact?: boolean
  list?: boolean
}>()

const printingAppStore = useUserPrintingAppStore()

const hasFreeBoards = computed(() => boards && boards.filter(board => board.free).length > 0)

const loadingWriters = ref(false)

function getAvailableWriters() {
  if (game.concluded_at || !game.tournament_id) {
    return
  }
  loadingWriters.value = true

  callAction(game.tournament_id, 'get-writers', {
    game_id: game.id,
  }).then((data) => {
    useModal().open(WriterSelectionModal, {
      game: game,
      availableWriters: data,
    })
  }).finally(() => {
    loadingWriters.value = false
  })
}

function clearBoard() {
  if (game.tournament_id === null) {
    return
  }
  $api('/tournaments/games/clear-board', {
    method: 'POST',
    body: {
      game_id: game.id,
    },
  }).then(() => {
    boardMenuOpen.value = false
  })
}

function assignBoard(board: Board) {
  if (game.tournament_id === null) {
    return
  }
  $api('/tournaments/games/assign-board', {
    method: 'POST',
    body: {
      game_id: game.id,
      board_id: board.id,
    },
  }).then(() => {
    boardMenuOpen.value = false
  })
}

const hasCurrentPlayer = computed(() => {
  return authStore.user?.player_id === getPlayer(game.player1).id || authStore.user?.player_id === getPlayer(game.player2).id
})

const isRecentCall = computed(() => {
  if (!game.called_at) return false
  const callTime = new Date(game.called_at)
  const now = new Date()
  const diffInMinutes = (now.getTime() - callTime.getTime()) / (1000 * 60)
  return diffInMinutes < 10
})

const scoringFormOpen = ref(false)
const boardMenuOpen = ref(false)
</script>

<template>
  <div
    v-if="list"
    class="flex flex-row hover:bg-primary-100 dark:hover:bg-primary-900 border-l-8 pl-2"
    :style="{ borderLeftColor: game.color_code }"
  >
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
      <div class="col-span-2">
        <div class="flex flex-row gap-1 text-sm">
          {{ $t('Tournament:') }}
          <span
            class="font-bold px-2 rounded border-2"
            :style="{ borderColor: game.color_code }"
          >{{ game.tournament_name }}</span>
          |
          <span class="font-bold">
            <template v-if="game.phase === 'elimination'">{{ $t('Elimination phase') }}</template>
            <template v-else>{{ $t('Group phase') }}</template>
          </span>
          <div>
            - {{ game.round_label }}
          </div>
          <div>
            <UiBadge
              v-if="game.called_at && !game.concluded_at && !game.bull_winner"
              class="flex flex-row gap-2 !text-xs"
              :variant="isRecentCall ? 'success' : 'destructive'"
            >
              {{ $t('Called:') }}
              <NuxtTime
                relative
                :datetime="game.called_at"
              />
            </UiBadge>
            <UiBadge
              v-else-if="!game.concluded_at && game.bull_winner"
              class="flex flex-row gap-2 !text-sm"
              variant="success"
            >
              {{ $t('Started') }}
            </UiBadge>
            <UiBadge
              v-if="game.can_assign_board && !hasFreeBoards"
              variant="destructive"
            >
              {{ $t('No free board') }}
            </UiBadge>
            <UiBadge
              v-if="game.cannot_play_reason"
              variant="destructive"
            >
              {{ game.cannot_play_reason }}
            </UiBadge>
          </div>
        </div>
        <div class="max-w-lg">
          <game-player
            :game="game"
            :player="game.player1"
            :show-parent-group="game.elimination_round === 1"
          />
          <game-player
            :game="game"
            :player="game.player2"
            :show-parent-group="game.elimination_round === 1"
          />
        </div>
        <div class="text-sm">
          {{ $t('Writer:') }}
          <span class="font-bold">{{ game.writer?.name || 'TBD' }}</span>
        </div>
      </div>
      <div class="col-span-1 flex items-center flex-row gap-1 flex-wrap">
        <!-- Assign board button -->
        <UiDropdownMenu>
          <UiDropdownMenuTrigger as-child>
            <UiButton
              v-if="!game.cannot_play_reason && hasFreeBoards && game.can_assign_board || game.board"
              size="sm"
              :variant="game.board ? 'warning' : 'success'"
            >
              <template v-if="!game.board && !game.concluded_at">
                {{ $t('Assign board') }}
              </template>
              <template v-else-if="game.board">
                {{ game.board.name }}
              </template>
            </UiButton>
          </UiDropdownMenuTrigger>
          <UiDropdownMenuContent>
            <div class="p-4 flex flex-col gap-4">
              <UiAlert
                v-if="game.writer.id === 0 && !game.board"
                variant="warning"
                :title="$t('There is no writer assigned for this game.')"
              />
              <div
                v-if="game.can_assign_board"
                class="grid grid-cols-4 gap-2"
              >
                <UiButton
                  v-for="board in boards"
                  :key="board.id"
                  :text="board.name"
                  :disabled="!board.free || !board.available"
                  @click="assignBoard(board)"
                />
              </div>
              <template v-if="game.board">
                <UiButton
                  variant="destructive"
                  :text="$t('Clear board')"
                  @click="clearBoard()"
                />
              </template>
            </div>
          </UiDropdownMenuContent>
        </UiDropdownMenu>
        <!-- Enter scores button -->
        <UiDropdownMenu>
          <UiDropdownMenuTrigger as-child>
            <UiButton size="sm">
              {{ $t('Enter scores') }}
            </UiButton>
          </UiDropdownMenuTrigger>
          <UiDropdownMenuContent v-if="!forDisplay">
            <EnterScoreForm
              :game="game"
              @score-updated="scoringFormOpen = false"
            />
          </UiDropdownMenuContent>
        </UiDropdownMenu>
        <UiButton
          v-if="!game.called_at && game.board"
          variant="success"
          size="sm"
          @click="markGameAsCalled(game)"
        >
          {{ $t('Mark as called') }}
        </UiButton>
        <UiButton
          size="sm"
          @click="printingAppStore.directPrint(game.tournament_id, game)"
        >
          <template v-if="game.printed">
            <Icon
              name="tabler-check"
              class="w-4 h-4 text-green-500"
            />
            {{ $t('Printed') }}
          </template>
          <template v-else>
            <Icon
              name="tabler-printer"
              class="w-4 h-4"
            />
            {{ $t('Print') }}
          </template>
        </UiButton>
        <UiButton
          :variant="game.writer.id !== 0 ? 'secondary' : 'warning'"
          size="sm"
          @click="getAvailableWriters()"
          @mouseenter="activePlayerId = game.writer.id !== 0 ? game.writer.id : null"
          @mouseleave="activePlayerId = null"
        >
          <template v-if="game.writer.id !== 0">
            {{ $t('Change writer') }}
          </template>
          <template v-else>
            {{ $t('Assign writer') }}
          </template>
        </UiButton>
      </div>
    </div>
  </div>
  <!-- ############################################################################### -->
  <!-- ############################################################################### -->
  <!-- ############################################################################### -->
  <!-- ############################################################################### -->
  <div
    v-else
    :class="{ 'opacity-50': game.has_bye, 'hidden': game.has_bye && compact }"
    class="max-w-full"
  >
    <div
      v-if="forDisplay && game.board"
      class="mb-0.5 flex flex-row gap-0.5 justify-end flex-1"
    >
      <UiBadge
        v-if="game.board.name"
        variant="outline"
        :label="game.board.name"
      />
      <UiBadge
        v-if="game.live_scores"
        variant="success"
        :label="$t('Live scores')"
      />
      <UiBadge
        v-else
        variant="default"
        :label="$t('In play')"
      />
    </div>
    <div
      v-if="!forDisplay"
      :id="'game-' + game.id + '|' + game.game_number"
      class="grid grid-cols-3 -top-2 left-0 w-full cursor-pointer text-sm h-[20px]"
    >
      <div class="w-full flex-grow justify-start col-span-2">
        <UiTooltip v-if="game.cannot_play_reason">
          <UiTooltipTrigger>
            <Icon
              class="text-orange-800"
              name="tabler-alert-circle"
            />
          </UiTooltipTrigger>
          <UiTooltipContent>
            {{ game.cannot_play_reason }}
          </UiTooltipContent>
        </UiTooltip>
        <UiTooltip v-if="game.can_assign_board && !hasFreeBoards">
          <UiTooltipTrigger>
            <Icon
              class="text-orange-800"
              name="tabler-alert-circle"
            />
          </UiTooltipTrigger>
          <UiTooltipContent>
            {{ $t('No free board') }}
          </UiTooltipContent>
        </UiTooltip>

        <UiDropdownMenu
          v-else-if="hasFreeBoards && game.can_assign_board || game.board"
          v-model:open="boardMenuOpen"
        >
          <UiDropdownMenuTrigger
            class="text-xs bg-green-500 text-green-900 px-2 rounded-t"
            :class="{ 'bg-yellow-500 text-yellow-900': game.board }"
          >
            <template v-if="!game.board && !game.concluded_at">
              {{ $t('Assign board') }}
            </template>
            <template v-else-if="game.board">
              <span class="text-[12px]">
                {{ game.board.name }}
              </span>
            </template>
          </UiDropdownMenuTrigger>
          <UiDropdownMenuContent>
            <div class="p-4 flex flex-col gap-4">
              <UiAlert
                v-if="game.writer.id === 0 && !game.board"
                variant="warning"
                :title="$t('There is no writer assigned for this game.')"
              />
              <div
                v-if="game.can_assign_board"
                class="grid grid-cols-4 gap-2"
              >
                <UiButton
                  v-for="board in boards"
                  :key="board.id"
                  :text="board.name"
                  :disabled="!board.free || !board.available"
                  @click="assignBoard(board)"
                />
              </div>
              <template v-if="game.board">
                <UiButton
                  variant="destructive"
                  :text="$t('Clear board')"
                  @click="clearBoard()"
                />
              </template>
            </div>
          </UiDropdownMenuContent>
        </UiDropdownMenu>
      </div>
      <div class="text-right flex flex-row justify-end truncate col-span-1">
        <div
          class="pl-2"
          @click="printingAppStore.directPrint(game.tournament_id, game)"
        >
          <Icon
            name="tabler-printer"
            class="w-4 h-4"
          />
        </div>
      </div>
    </div>
    <template v-if="forDisplay">
      <NuxtLink
        v-if="game.bull_winner"
        :to="localePath(`/live/${game.id}`)"
      >
        <game-player
          :game="game"
          :player="game.player1"
          :view-only="true"
        />
        <game-player
          :game="game"
          :player="game.player2"
          :view-only="true"
        />
      </NuxtLink>
      <NuxtLink
        v-else
        :to="localePath(`/tournaments/${game.tournament_id}/games/${game.id}`)"
      >
        <game-player
          :game="game"
          :player="game.player1"
        />
        <game-player
          :game="game"
          :player="game.player2"
        />
      </NuxtLink>
    </template>
    <template v-else>
      <div
        class="flex flex-col max-w-full cursor-pointer w-full rounded-l"
        :class="{ 'ring-2 rounded-l ring-yellow-400': hasCurrentPlayer }"
      >
        <UiDropdownMenu
          v-model:open="scoringFormOpen"
          :modal="false"
        >
          <UiDropdownMenuTrigger as-child>
            <div>
              <game-player
                :game="game"
                :player="game.player1"
                :show-parent-group="game.elimination_round === 1"
              />
              <game-player
                :game="game"
                :player="game.player2"
                :show-parent-group="game.elimination_round === 1"
              />
            </div>
          </UiDropdownMenuTrigger>
          <UiDropdownMenuContent v-if="!forDisplay">
            <EnterScoreForm
              :game="game"
              @score-updated="scoringFormOpen = false"
            />
          </UiDropdownMenuContent>
        </UiDropdownMenu>
      </div>
    </template>
    <div class="text-right flex flex-row justify-end text-xs">
      <UiTooltip>
        <UiTooltipTrigger as-child>
          <button
            class="rounded-b px-2 overflow-hidden max-w-[75%] -mt-px"
            :class="{
              'bg-teal-600 text-white': game.writer.id !== 0,
              'cursor-pointer': !forDisplay,
              '!bg-teal-400': game.writer.id === activePlayerId,
            }"
            @click="!forDisplay && getAvailableWriters()"
            @mouseenter="activePlayerId = game.writer.id !== 0 ? game.writer.id : null"
            @mouseleave="activePlayerId = null"
          >
            <div class="overflow-hidden truncate">
              {{ game.writer.name }}
            </div>
          </button>
        </UiTooltipTrigger>
        <UiTooltipContent>
          {{ game.writer.name }}
        </UiTooltipContent>
      </UiTooltip>
    </div>
  </div>
</template>
